import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { GridService } from '../shared/grid/grid.service';
import { ExposureDetailRequest, ExposureRequest, TaskContributionRequest } from '../models/exposureRequest.model';
import { ExposureByLocationGroup, ExposureBySEGs, ExposureByTask, ExposureByTaskGroup, ExposureForSegByOrgLevel, ExposureTaskDistribution, ExposureTaskSample, SegAssignRequest, WorkerExposure } from '../models/exposureBySeg.model';
import { SEG, SegRecordHistory } from '../models/seg.model';
import { map, Observable } from 'rxjs';
import { GridModel } from '../shared/grid/grid.model';
import { ColumnDetails } from '../models/columnDetails.model';
import nameOf from '../shared/form/nameOf.helper';
import { OperatorTypeEnum } from '../enums/operatorType.enum';
import { SegMergeDeleteRequest } from '../models/segMergeDeleteRequest.model';

@Injectable({
  providedIn: 'root'
})
export class SegService extends GridService<SEG> {

  constructor(http: HttpClient) {
    super('Seg', http);
  }

  getExposureBySEGs(exposureRequest: ExposureRequest) {
    return this.http.post<ExposureBySEGs[]>(this.getApiUrl('Seg/GetExposureBySeg'), exposureRequest);
  }

  getExposureForSegByOrgLevel(exposureRequest: ExposureDetailRequest) {
    return this.http.post<ExposureForSegByOrgLevel[]>(this.getApiUrl('Seg/GetExposureForSegByOrgLevel'), exposureRequest);
  }

  getAllSegsForTaskGroup(id: any) {
    return this.http.get<SEG[]>(this.getApiUrl('Seg/GetAllSegsForTaskGroup?taskGroupId=' + id));
  }

  getWorkerExposure(exposureRequest: ExposureDetailRequest) {
    return this.http.post<WorkerExposure[]>(this.getApiUrl('Seg/GetWorkerExposure'), exposureRequest);
  }

  getExposureByTask(exposureRequest: ExposureDetailRequest) {
    return this.http.post<ExposureByTask[]>(this.getApiUrl('Task/GetExposureByTask'), exposureRequest);
  }

  getExposureForTaskGroupByOrgLevel(exposureRequest: ExposureDetailRequest) {
    return this.http.post<ExposureTaskDistribution[]>(this.getApiUrl('Task/GetExposureForTaskGroupByOrgLevel'), exposureRequest);
  }

  getExposureByTaskGroup(exposureRequest: ExposureDetailRequest) {
    return this.http.post<ExposureByTaskGroup[]>(this.getApiUrl('Task/GetExposureContributionByTaskGroup'), exposureRequest);
  }

  getExposureForTaskByOrgLevel(exposureRequest: ExposureDetailRequest) {
    return this.http.post<ExposureTaskDistribution[]>(this.getApiUrl('Task/GetExposureForTaskByOrgLevel'), exposureRequest);
  }

  getSampleExposureForTaskByOrgLevel(exposureRequest: ExposureDetailRequest) {
    return this.http.post<ExposureTaskSample[]>(this.getApiUrl('Task/GetSampleExposureForTaskByOrgLevel'), exposureRequest);
  }

  getExposureContributionByLocationGroup(exposureRequest: ExposureDetailRequest) {
    return this.http.post<ExposureByLocationGroup[]>(this.getApiUrl('Location/GetExposureContributionByLocationGroup'), exposureRequest);
  }

  assignTaskGroup(segAssignRequest: SegAssignRequest) {
    return this.http.post<SegAssignRequest>(this.getApiUrl('Seg/AssignTaskGroup'), segAssignRequest);
  }

  getHistory(params: HttpParams): Observable<GridModel<SegRecordHistory>> {
    return this.http.get<GridModel<SegRecordHistory>>(this.getApiUrl(`${this.controllerName}/GetHistory`), {
      params: params
    });
  }

  getHistoryFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
    return this.http.get<{ [property: string]: ColumnDetails }>(
      this.getApiUrl(`${this.controllerName}/GetHistoryFieldConfiguration`)
    );
  }

  getDeletedList(filter: string, orderBy: string, sort: string, page: number, pageSize: number): Observable<GridModel<SEG>> {
    return this.http.get<GridModel<SEG>>(this.getApiUrl(`${this.controllerName}/GetDeletedList`), {
      params: this.createParamsWithFilterBy(filter, orderBy, sort, page, pageSize, '{}'),
    });
  }

  restore(id: string): Observable<void> {
    return this.http.post<void>(this.getApiUrl(`${this.controllerName}/Restore/${id}`), {});
  }

  // DASH-3000 AC2/AC3: candidates the picker may offer are scoped server-side to the same
  // organization level; excludes the source SEG and any SEG already merged into another are
  // filtered client-side as a defense-in-depth (BE also enforces this on MergeAndDelete, AC6).
  getMergeCandidates(organizationLevelId: string, excludeSegId: string): Observable<SEG[]> {
    const field = nameOf<SEG>(x => x.organizationLevelId);
    const filterBy = JSON.stringify({
      [`OperatorTypeEnum.${OperatorTypeEnum.Equal}_${field}`]: JSON.stringify({
        Condition: OperatorTypeEnum.Equal,
        Field: field,
        Value: organizationLevelId,
      }),
    });

    return this.get('', nameOf<SEG>(x => x.name), 'asc', 0, 1000, filterBy).pipe(
      map((response) => (response?.data ?? []).filter(seg => seg.id !== excludeSegId && !seg.mergedIntoId)),
    );
  }

  mergeAndDelete(id: string, mergeIntoId: string): Observable<void> {
    const payload: SegMergeDeleteRequest = { id, mergeIntoId };
    return this.http.post<void>(this.getApiUrl(`${this.controllerName}/MergeAndDelete`), payload);
  }

}
